import { useQuery } from '@tanstack/react-query'
import { Table, Tooltip } from 'antd'
import dayjs from 'dayjs'
import React from 'react'

import $api from '../../../../API'
import type { ClaimStatusHistory } from '../types'

interface ClaimStatusHistoryTooltipProps {
  claimId: string
  children: React.ReactElement
}

export function ClaimStatusHistoryTooltip({ claimId, children }: ClaimStatusHistoryTooltipProps) {
  const { data: statusHistory, isLoading } = useQuery({
    queryKey: ['claim-status-history', claimId],
    queryFn: async (): Promise<ClaimStatusHistory[]> => {
      const response = await $api.get(`/claims/${claimId}/status-history`)
      return response.data
    },
    enabled: !!claimId
  })

  const columns = [
    {
      title: 'Claim ID',
      dataIndex: 'nomi_claim_id',
      key: 'nomi_claim_id',
      width: 120,
      render: (value: string) => value || '-'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100
    },
    {
      title: 'Last Update',
      dataIndex: 'last_update_ts',
      key: 'last_update_ts',
      width: 140,
      render: (value: string) => {
        if (!value) return '-'
        return dayjs(value).format('MM/DD/YYYY HH:mm:ss')
      }
    },
    {
      title: 'Exception Type',
      dataIndex: 'exception_type',
      key: 'exception_type',
      width: 120,
      render: (value: string) => value || '-'
    },
    {
      title: 'Exception Reason',
      dataIndex: 'exception_reason_type',
      key: 'exception_reason_type',
      width: 140,
      render: (value: string) => value || '-'
    },
    {
      title: 'Source File',
      dataIndex: 'source_file_name',
      key: 'source_file_name',
      width: 200,
      render: (value: string) => value || '-'
    },
    {
      title: 'Nomi File Receipt',
      dataIndex: 'nomi_file_receipt_ts',
      key: 'nomi_file_receipt_ts',
      width: 140,
      render: (value: string) => {
        if (!value) return '-'
        return dayjs(value).format('MM/DD/YYYY HH:mm:ss')
      }
    }
  ]

  const tooltipContent = (
    <div style={{ maxWidth: '900px' }}>
      <div className='mb-2 font-semibold'>Claim Status History</div>
      {isLoading ? (
        <div>Loading...</div>
      ) : statusHistory && statusHistory.length > 0 ? (
        <Table
          dataSource={statusHistory}
          columns={columns}
          pagination={false}
          size='small'
          rowKey='id'
          scroll={{ x: 800 }}
          className='status-history-table'
        />
      ) : (
        <div>No status history available for this claim</div>
      )}
    </div>
  )

  return (
    <Tooltip
      title={tooltipContent}
      color='#ffffff'
      styles={{
        root: {
          maxWidth: '950px'
        }
      }}
      placement='bottomLeft'
      trigger='hover'
    >
      {children}
    </Tooltip>
  )
}
